/**
 * Deal Flow — retail-arbitrage opportunity list backed by the real persisted
 * sourcing pipeline (product_sourcing_opportunities). No demo fixtures, no
 * frontend business data, zero provider calls on load.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Filter,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
  ShoppingCart,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usd } from "@/lib/format";
import { fetchDealFlowLeads, saveOpportunity } from "@/lib/dealFlow/dealFlowApi";
import { api } from "@/lib/api";
import { DealFlowCard } from "./DealFlowCard";
import { DealFlowTable } from "./DealFlowTable";
import { OpportunityDetailDialog } from "./OpportunityDetailDialog";
import "@/styles/deal-flow.css";

const PAGE_SIZE = 24;
const LIVE_POLL_MS = 45_000;

const SORTS = [
  { k: "newest", nm: "Newest discovered" },
  { k: "updated_desc", nm: "Recently updated" },
  { k: "spread_desc", nm: "Highest spread" },
  { k: "confidence_desc", nm: "Highest confidence" },
  { k: "source_price_asc", nm: "Lowest source price" },
  { k: "source_price_desc", nm: "Highest source price" },
  { k: "walmart_price_desc", nm: "Highest Walmart price" },
  { k: "walmart_price_asc", nm: "Lowest Walmart price" },
];

// Result-query preset chips. Prefer opportunity_status when present; fall back
// to legacy match.status for older documents without status enrichment.
const PRESET_CHIPS = [
  { id: "all", label: "All", patch: {} },
  { id: "winning", label: "Winning", patch: { opportunity_status: "WINNING", status: "" } },
  { id: "estimated", label: "Estimated", patch: { opportunity_status: "ESTIMATED", status: "" } },
  { id: "needs_review", label: "Needs Review", patch: { opportunity_status: "NEEDS_REVIEW", status: "" } },
];

const STATUS_OPTIONS = [
  { v: "", nm: "Any status" },
  { v: "verified", nm: "Verified" },
  { v: "exact", nm: "Exact" },
  { v: "probable", nm: "Probable" },
  { v: "review", nm: "Needs review" },
];

const AGE_OPTIONS = [
  { v: "", nm: "Any time" },
  { v: "1", nm: "Today" },
  { v: "7", nm: "Last 7 days" },
  { v: "30", nm: "Last 30 days" },
];

/** Stable, real filter state (server query params). */
const EMPTY_FILTERS = {
  q: "",
  status: "",
  opportunity_status: "",
  source: "",
  category: "",
  brand: "",
  min_source_price: "",
  max_source_price: "",
  min_walmart_price: "",
  max_walmart_price: "",
  min_spread: "",
  max_spread: "",
  min_confidence: "",
  min_roi: "",
  min_profit: "",
  availability: "",
  age_days: "",
};

// Availability filter options (only real persisted Walmart availability states).
const AVAILABILITY_OPTIONS = [
  { v: "", nm: "Any availability" },
  { v: "In stock", nm: "In stock" },
  { v: "Out of stock", nm: "Out of stock" },
];

function numOrEmpty(v) {
  return v === "" || v == null ? "" : String(v);
}

export function DealFlowScreen({ title = "Quick Winner" }) {
  const nav = useNavigate();

  const [applied, setApplied] = useState({ ...EMPTY_FILTERS });
  const [draft, setDraft] = useState({ ...EMPTY_FILTERS });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [view, setView] = useState("cards");
  const [sort, setSort] = useState("newest");
  const [sortOpen, setSortOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // Distinct initial-load vs background-refresh states so a transient Live Feed
  // poll failure never blanks already-rendered cards.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [initialError, setInitialError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);

  const [liveFeed, setLiveFeed] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState(null);
  const reqRef = useRef(0);

  const buildParams = useCallback(
    (p) => {
      const out = {
        page: p,
        limit: PAGE_SIZE,
        sort,
      };
      for (const k of Object.keys(EMPTY_FILTERS)) {
        const v = applied[k];
        if (v !== "" && v != null) out[k] = v;
      }
      return out;
    },
    [sort, applied],
  );

  const load = useCallback(
    async (opts = {}) => {
      const { background = false } = opts;
      const gen = ++reqRef.current;
      if (background) {
        setRefreshing(true);
        setRefreshError(null);
      } else {
        setLoading(true);
        setInitialError(null);
      }
      try {
        const res = await fetchDealFlowLeads(buildParams(page));
        if (gen !== reqRef.current) return; // stale response discarded
        setLeads(res.leads);
        setTotal(res.total);
        setLastUpdatedAt(res.lastUpdatedAt);
        setLastPolledAt(new Date());
        if (background) setRefreshError(null);
      } catch (err) {
        if (gen !== reqRef.current) return;
        const msg = err?.response?.data?.detail || err?.message || "Unable to load Quick Winner.";
        if (background) {
          // Keep existing leads/total; show a subtle refresh error only.
          setRefreshError(msg);
        } else {
          setLeads([]);
          setTotal(0);
          setInitialError(msg);
        }
      } finally {
        if (gen === reqRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [buildParams, page],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Live Feed = poll the persisted API. Never triggers provider discovery.
  // Polls run as background refreshes: a transient failure keeps existing cards.
  useEffect(() => {
    if (!liveFeed) return undefined;
    const t = setInterval(() => {
      void load({ background: true });
    }, LIVE_POLL_MS);
    return () => clearInterval(t);
  }, [liveFeed, load]);

  const applyFilters = () => {
    setApplied({ ...draft });
    setQuery(draft.q);
    setPage(1);
    setDrawerOpen(false);
  };

  const clearFilters = () => {
    setDraft({ ...EMPTY_FILTERS });
    setApplied({ ...EMPTY_FILTERS });
    setQuery("");
    setPage(1);
    setDrawerOpen(false);
  };

  const activeFilterCount = useMemo(() => {
    return Object.keys(EMPTY_FILTERS).filter((k) => {
      const v = applied[k];
      return v !== "" && v != null;
    }).length;
  }, [applied]);

  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, maxPage);

  const [detailIndex, setDetailIndex] = useState(null);
  const [history, setHistory] = useState([]);

  const openDetail = (lead) => {
    const idx = leads.findIndex((l) => l.id === lead.id);
    if (idx < 0) return;
    setDetailIndex(idx);
    setHistory([]);
    // Load persisted history (Mongo only, 0 provider calls).
    api.productSourcingHistory(lead.id)
      .then((res) => {
        const data = res?.data ?? res ?? {};
        setHistory(Array.isArray(data.items) ? data.items : []);
      })
      .catch(() => setHistory([]));
  };

  const onSave = async (lead) => {
    const next = !lead.saved;
    // Optimistic, with safe rollback on failure. Uses the existing Product Save
    // endpoint (workspace-scoped sfh_saved_products) — never mutates opportunity
    // freshness.
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, saved: next } : l)));
    try {
      await saveOpportunity(lead, next);
      toast.success(next ? "Saved" : "Removed from saved");
    } catch (err) {
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, saved: !next } : l)));
      toast.error(err?.response?.data?.detail || "Couldn't save. Try again.");
    }
  };

  const onRefresh = async (lead) => {
    await api.productSourcingRefresh(lead.id);
    // Reload the feed to reflect the enriched metadata (Mongo read).
    await load();
  };

  const onAnalyze = (lead) => {
    openDetail(lead);
  };

  const filterSummary = useMemo(() => {
    const parts = [];
    if (applied.min_spread) parts.push(`Spread ≥ ${usd(Number(applied.min_spread))}`);
    if (applied.max_spread) parts.push(`Spread ≤ ${usd(Number(applied.max_spread))}`);
    if (applied.min_confidence) parts.push(`Confidence ≥ ${applied.min_confidence}%`);
    if (applied.availability) parts.push(applied.availability);
    if (applied.min_source_price && applied.max_source_price)
      parts.push(`Source ${usd(Number(applied.min_source_price))}–${usd(Number(applied.max_source_price))}`);
    else if (applied.min_source_price) parts.push(`Source ≥ ${usd(Number(applied.min_source_price))}`);
    else if (applied.max_source_price) parts.push(`Source ≤ ${usd(Number(applied.max_source_price))}`);
    if (applied.min_walmart_price && applied.max_walmart_price)
      parts.push(`Walmart ${usd(Number(applied.min_walmart_price))}–${usd(Number(applied.max_walmart_price))}`);
    else if (applied.min_walmart_price) parts.push(`Walmart ≥ ${usd(Number(applied.min_walmart_price))}`);
    else if (applied.max_walmart_price) parts.push(`Walmart ≤ ${usd(Number(applied.max_walmart_price))}`);
    if (applied.status) parts.push(STATUS_OPTIONS.find((s) => s.v === applied.status)?.nm || applied.status);
    if (applied.category) parts.push(applied.category);
    if (applied.brand) parts.push(applied.brand);
    if (applied.source) parts.push(applied.source);
    if (applied.age_days) parts.push(AGE_OPTIONS.find((a) => a.v === applied.age_days)?.nm || `Last ${applied.age_days} days`);
    if (applied.q) parts.push(`“${applied.q}”`);
    return parts;
  }, [applied]);

  return (
    <div className="df-root" data-testid="deal-flow">
      <header className="mb-1">
        <h1 className="text-ptitle text-ink-primary m-0">{title}</h1>
      </header>

      {/* Control bar */}
      <div className="mt-4 bg-white border border-edge-subtle rounded-xl px-3 py-2.5 flex items-center gap-3 flex-wrap shadow-float">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 border border-edge-subtle bg-white rounded-lg px-2.5 py-1.5 text-compact font-semibold hover:bg-surface-1 cursor-default"
          title="Source retailers → Walmart"
        >
          <ShoppingCart className="w-4 h-4 text-slate-600" />
          <span className="text-ink-muted">→</span>
          <span className="text-base leading-none">🇺🇸</span>
          <span className="font-bold text-sm tracking-tight text-[#0071dc]">Walmart</span>
        </button>

        <span className="w-px h-5 bg-edge-subtle" />

        <div className="flex gap-1.5 flex-wrap items-center min-w-0">
          {filterSummary.length ? (
            filterSummary.map((label) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 border border-edge-subtle bg-surface-1 rounded-full px-2.5 py-1 text-xs text-slate-600 whitespace-nowrap"
              >
                {label}
              </span>
            ))
          ) : (
            <span className="text-[12.5px] text-ink-muted">Showing all opportunities</span>
          )}
        </div>

        <span className="flex-1" />

        <button
          type="button"
          onClick={() => setLiveFeed((v) => !v)}
          aria-pressed={liveFeed}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-compact font-medium border",
            liveFeed
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : "border-edge-subtle bg-white text-ink-muted hover:bg-surface-1",
          )}
          data-testid="deal-flow-live-feed"
        >
          <RefreshCw className={cn("w-3.5 h-3.5", liveFeed && "text-emerald-700")} />
          {liveFeed ? "Auto-refresh on" : "Auto-refresh"}
        </button>

        <button
          type="button"
          title="Search"
          aria-label="Search"
          data-testid="deal-flow-search-toggle"
          onClick={() => setSearchOpen((s) => !s)}
          className="w-10 h-10 rounded-full border border-edge-subtle bg-white grid place-items-center text-slate-600 hover:bg-surface-1"
        >
          <Search className="w-[18px] h-[18px]" />
        </button>

        <button
          type="button"
          onClick={() => {
            setDraft({ ...applied });
            setDrawerOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-signal-blue text-white text-compact font-semibold hover:bg-blue-700"
        >
          <Filter className="w-[15px] h-[15px]" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-white/25 rounded-full px-1.5 text-[11px] font-bold min-w-[20px] text-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Preset chips — result-query presets only (0 provider calls). */}
      <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="deal-flow-presets">
        {PRESET_CHIPS.map((chip) => {
          const active = Object.keys(chip.patch).every(
            (k) => applied[k] === chip.patch[k]
          );
          return (
            <button
              key={chip.id}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setDraft((d) => ({ ...d, ...chip.patch }));
                setApplied((a) => ({ ...a, ...chip.patch }));
                setPage(1);
              }}
              className={cn(
                "inline-flex items-center rounded-full border px-3 py-1 text-[12.5px] font-semibold",
                active
                  ? "border-navy-900 bg-navy-900 text-white"
                  : "border-edge-subtle bg-white text-ink-muted hover:bg-surface-1",
              )}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-[11px] text-ink-muted m-0" data-testid="deal-flow-status-legend">
        Winning = verified offer + source stock + complete fulfillment economics · Estimated = promising match with outstanding stock or incomplete fees · Needs Review = missing identity, price, or cost data
      </p>

      {/* Search row */}
      {searchOpen && (
        <div className="mt-2.5 bg-white border border-edge-subtle rounded-xl px-4 py-2.5 flex items-center gap-3 shadow-float">
          <Search className="w-[18px] h-[18px] text-ink-muted shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setDraft((d) => ({ ...d, q: e.target.value }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setApplied((a) => ({ ...a, q: query }));
                setPage(1);
              }
              if (e.key === "Escape") {
                setSearchOpen(false);
                setQuery(applied.q);
              }
            }}
            placeholder="Search item ID, UPC, GTIN, brand, title, or merchant…"
            className="flex-1 border-0 bg-transparent text-[15px] outline-none"
            data-testid="deal-flow-search-input"
          />
          <button
            type="button"
            data-testid="deal-flow-search-submit"
            onClick={() => {
              setApplied((a) => ({ ...a, q: query }));
              setPage(1);
            }}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-navy-900 text-white text-[12.5px] font-semibold"
          >
            Search
          </button>
          <button
            type="button"
            aria-label="Close search"
            onClick={() => {
              setSearchOpen(false);
              setQuery(applied.q);
            }}
            className="w-8 h-8 rounded-full border border-edge-subtle grid place-items-center text-ink-muted"
          >
            ✕
          </button>
        </div>
      )}

      {/* Results bar */}
      <div className="flex items-center gap-3 pt-5 pb-3">
        <h2 className="m-0 text-base font-bold flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-signal-blue" /> Results
        </h2>
        <span className="text-compact text-ink-muted font-medium" data-testid="deal-flow-result-count">
          {`${total} ${total === 1 ? "opportunity" : "opportunities"}`}
        </span>
        {lastUpdatedAt && (
          <span className="text-[11px] text-ink-muted">
            · updated {new Date(lastUpdatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </span>
        )}
        {refreshing && <span className="text-[11px] text-ink-muted">· refreshing…</span>}
        {refreshError && (
          <span className="text-[11px] text-amber-700" data-testid="deal-flow-refresh-error">
            · {refreshError}
          </span>
        )}
        <span className="flex-1" />

        <div className="relative">
          <button
            type="button"
            title="Sort"
            aria-haspopup="true"
            aria-expanded={sortOpen}
            onClick={() => setSortOpen((s) => !s)}
            className={cn(
              "w-[34px] h-[34px] rounded-full border border-edge-subtle bg-white grid place-items-center relative hover:border-navy-900",
              sortOpen && "border-navy-900 bg-navy-900 text-white",
            )}
          >
            <ArrowUpDown className="w-[15px] h-[15px]" />
          </button>
          {sortOpen && (
            <div
              className="absolute z-[65] right-0 top-[calc(100%+9px)] bg-[#0B0F18] rounded-xl p-1 min-w-[220px] shadow-xl text-white"
              onClick={(e) => e.stopPropagation()}
            >
              {SORTS.map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => {
                    setSort(s.k);
                    setPage(1);
                    setSortOpen(false);
                  }}
                  className={cn(
                    "flex items-center gap-2 w-full border-0 bg-transparent px-3 py-2 rounded-lg text-[12.5px] text-left",
                    sort === s.k ? "bg-slate-700 text-white font-semibold" : "text-slate-200 hover:bg-slate-800",
                  )}
                >
                  {s.nm}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="inline-flex border border-edge-subtle rounded-lg overflow-hidden bg-white">
          {["cards", "table"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                "border-0 px-3.5 py-2 text-[12.5px] font-semibold capitalize",
                view === v ? "bg-navy-900 text-white" : "bg-white text-ink-muted",
              )}
            >
              {v}
            </button>
          ))}
        </span>
      </div>

      {/* Body */}
      {loading && !leads.length ? (
        <div className="py-16 text-center text-ink-muted" data-testid="deal-flow-loading">
          Loading opportunities…
        </div>
      ) : initialError && !leads.length ? (
        <div className="py-16 text-center" data-testid="deal-flow-error" role="alert">
          <p className="m-0 text-[15px] font-semibold text-ink-primary">{String(initialError)}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 px-3.5 py-2 rounded-lg border border-edge-subtle bg-white text-compact font-semibold"
          >
            Retry
          </button>
        </div>
      ) : !leads.length ? (
        activeFilterCount || applied.q ? (
          <div className="py-16 text-center" data-testid="deal-flow-filtered-empty">
            <p className="m-0 text-[15px] font-semibold text-ink-primary">No opportunities match these filters.</p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-4 px-3.5 py-2 rounded-lg border border-edge-subtle bg-white text-compact font-semibold"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="py-16 text-center text-ink-muted" data-testid="deal-flow-empty">
            <p className="m-0 text-[15px] font-semibold text-ink-primary">
              Quick Winner hasn't found an opportunity yet.
            </p>
            <p className="mt-2 mb-0 text-compact text-ink-muted">
              Run Auto Sourcing to search approved retailers, or adjust your filters.
            </p>
            <button
              type="button"
              onClick={() => nav("/app/research/auto-sourcing")}
              className="mt-4 px-4 py-2 rounded-lg bg-signal-blue text-white text-compact font-semibold"
              data-testid="deal-flow-open-auto-sourcing"
            >
              Open Auto Sourcing
            </button>
          </div>
        )
      ) : view === "cards" ? (
        <div className="df-grid grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(212px,1fr))] pb-4" data-testid="deal-flow-grid">
          {leads.map((lead) => (
            <DealFlowCard key={lead.id} lead={lead} onSave={onSave} onAnalyze={onAnalyze} onOpen={openDetail} />
          ))}
        </div>
      ) : (
        <DealFlowTable leads={leads} onAnalyze={onAnalyze} onOpen={openDetail} />
      )}

      {/* Pager */}
      {total > PAGE_SIZE && (
        <div className="df-pager" role="navigation" aria-label="Results pagination" data-testid="deal-flow-pager">
          <span className="df-pager-range text-compact text-ink-muted tabular-nums">
            <b className="text-ink-primary font-bold">{(safePage - 1) * PAGE_SIZE + 1}</b> to{" "}
            <b className="text-ink-primary font-bold">{Math.min(safePage * PAGE_SIZE, total)}</b> of{" "}
            <b className="text-ink-primary font-bold">{total.toLocaleString()}</b>
          </span>
          <span className="df-pager-nav ml-auto flex items-center gap-0.5">
            <PgBtn label="First page" disabled={safePage <= 1} onClick={() => setPage(1)}>
              <ChevronsLeft className="w-4 h-4" />
            </PgBtn>
            <PgBtn label="Previous page" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="w-4 h-4" />
            </PgBtn>
            <span className="flex items-center gap-2 text-compact text-ink-muted px-2">
              Page{" "}
              <span className="min-w-[26px] h-[26px] px-2 rounded-full bg-navy-900 text-white text-[12.5px] font-bold grid place-items-center tabular-nums">
                {safePage}
              </span>{" "}
              of <b className="text-ink-primary">{maxPage}</b>
            </span>
            <PgBtn label="Next page" disabled={safePage >= maxPage} onClick={() => setPage((p) => Math.min(maxPage, p + 1))}>
              <ChevronRight className="w-4 h-4" />
            </PgBtn>
            <PgBtn label="Last page" disabled={safePage >= maxPage} onClick={() => setPage(maxPage)}>
              <ChevronsRight className="w-4 h-4" />
            </PgBtn>
          </span>
        </div>
      )}

      {drawerOpen && (
        <FilterDrawer
          draft={draft}
          setDraft={setDraft}
          onApply={applyFilters}
          onClearAll={clearFilters}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {detailIndex != null && leads[detailIndex] && (
        <OpportunityDetailDialog
          lead={leads[detailIndex]}
          list={leads}
          index={detailIndex}
          onIndex={(i) => {
            setDetailIndex(i);
            setHistory([]);
            const next = leads[i];
            if (next) {
              api.productSourcingHistory(next.id)
                .then((res) => {
                  const data = res?.data ?? res ?? {};
                  setHistory(Array.isArray(data.items) ? data.items : []);
                })
                .catch(() => setHistory([]));
            }
          }}
          onClose={() => setDetailIndex(null)}
          onSave={onSave}
          onRefresh={onRefresh}
          history={history}
        />
      )}
    </div>
  );
}

function FilterDrawer({ draft, setDraft, onApply, onClearAll, onClose }) {
  const [mode, setMode] = React.useState("basic");
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const Field = ({ label, children }) => (
    <label className="block text-xs font-semibold mb-1.5">
      {label}
      {children}
    </label>
  );
  const inputCls = "w-full border border-edge-subtle rounded-lg px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-6 bg-black/60 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[min(560px,100%)] bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <header className="flex items-center gap-3 px-5 py-4 border-b border-edge-subtle">
          <h3 className="m-0 text-base font-bold flex-1">Filters</h3>
          <span className="inline-flex border border-edge-subtle rounded-lg overflow-hidden bg-white">
            {["basic", "advanced"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  "border-0 px-3 py-1.5 text-[12px] font-semibold capitalize",
                  mode === m ? "bg-navy-900 text-white" : "bg-white text-ink-muted",
                )}
              >
                {m}
              </button>
            ))}
          </span>
          <button type="button" aria-label="Close" onClick={onClose} className="w-[34px] h-[34px] rounded-full border border-edge-subtle grid place-items-center">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-5 overflow-y-auto" data-testid="deal-flow-filters">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Source merchant">
              <input className={inputCls} value={draft.source} onChange={set("source")} placeholder="e.g. Target" />
            </Field>
            <Field label="Category">
              <input className={inputCls} value={draft.category} onChange={set("category")} placeholder="e.g. Toys" />
            </Field>
            <Field label="Match status">
              <select className={inputCls} value={draft.status} onChange={set("status")}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.v} value={s.v}>{s.nm}</option>
                ))}
              </select>
            </Field>
            <Field label="Min spread">
              <input className={inputCls} type="number" value={draft.min_spread} onChange={set("min_spread")} placeholder="0" />
            </Field>
            <Field label="Discovered">
              <select className={inputCls} value={draft.age_days} onChange={set("age_days")}>
                {AGE_OPTIONS.map((a) => (
                  <option key={a.v} value={a.v}>{a.nm}</option>
                ))}
              </select>
            </Field>
          </div>

          {mode === "advanced" && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Brand">
                <input className={inputCls} value={draft.brand} onChange={set("brand")} placeholder="e.g. LEGO" />
              </Field>
              <Field label="Source price min">
                <input className={inputCls} type="number" min="0" value={draft.min_source_price} onChange={set("min_source_price")} placeholder="0" />
              </Field>
              <Field label="Source price max">
                <input className={inputCls} type="number" min="0" value={draft.max_source_price} onChange={set("max_source_price")} placeholder="any" />
              </Field>
              <Field label="Walmart price min">
                <input className={inputCls} type="number" min="0" value={draft.min_walmart_price} onChange={set("min_walmart_price")} placeholder="0" />
              </Field>
              <Field label="Walmart price max">
                <input className={inputCls} type="number" min="0" value={draft.max_walmart_price} onChange={set("max_walmart_price")} placeholder="any" />
              </Field>
              <Field label="Max spread">
                <input className={inputCls} type="number" value={draft.max_spread} onChange={set("max_spread")} placeholder="any" />
              </Field>
              <Field label="Min confidence">
                <input className={inputCls} type="number" min="0" max="100" value={draft.min_confidence} onChange={set("min_confidence")} placeholder="0" />
              </Field>
              <Field label="Walmart availability">
                <select className={inputCls} value={draft.availability} onChange={set("availability")}>
                  {AVAILABILITY_OPTIONS.map((a) => (
                    <option key={a.v} value={a.v}>{a.nm}</option>
                  ))}
                </select>
              </Field>
            </div>
          )}
        </div>
        <footer className="flex justify-between gap-2 px-5 py-4 border-t border-edge-subtle">
          <button type="button" onClick={onClearAll} className="px-4 py-2 rounded-lg border border-edge-subtle text-compact font-semibold">
            Clear all
          </button>
          <button type="button" onClick={onApply} className="px-4 py-2 rounded-lg bg-signal-blue text-white text-compact font-semibold">
            Apply filters
          </button>
        </footer>
      </div>
    </div>
  );
}

function PgBtn({ children, label, disabled, onClick }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="w-8 h-8 rounded-lg border border-transparent grid place-items-center text-slate-600 disabled:opacity-30 hover:enabled:bg-surface-1 hover:enabled:border-edge-subtle"
    >
      {children}
    </button>
  );
}

export default DealFlowScreen;
